import {
  FEATURE_KEYS_CATALOG,
  PERMISSION_AREA_KEYS,
  PERMISSION_SCOPE_KEYS,
  type FeatureKey,
  type OrganizationRole,
  type PermissionAction,
  type PermissionArea,
  type PermissionScope,
} from "@gioia365/contracts";
import type { MessageKey } from "@gioia365/i18n";
import type { Translate } from "../../i18n/locale";
import { AREA_LABEL, FEATURE_GROUPS, FEATURE_LABEL, areaActions } from "./permission-labels";

// Un ruolo spiegato a parole (piano M1b Task 8, «Chi può fare cosa, in parole»): una frase per ogni area del
// programma, poi le funzioni speciali e le sezioni delle Impostazioni. Si ricava dai permessi salvati, quindi
// cambia da sola quando cambiano.

type Powers = Pick<OrganizationRole, "areaPermissions" | "features">;

// Come si dice ogni ambito di ogni azione; «creare» non ha «solo i suoi».
const CLAUSE: Record<PermissionAction, Partial<Record<PermissionScope, MessageKey>>> = {
  view: { none: "roles.words.view.none", own: "roles.words.view.own", all: "roles.words.view.all" },
  create: { none: "roles.words.create.none", all: "roles.words.create.all" },
  update: { none: "roles.words.update.none", own: "roles.words.update.own", all: "roles.words.update.all" },
  delete: { none: "roles.words.delete.none", own: "roles.words.delete.own", all: "roles.words.delete.all" },
};

/** L'ambito salvato per un'area e un'azione; assente o sconosciuto vale «nessuno», come per il server. */
function scopeOf(powers: Powers, area: PermissionArea, action: PermissionAction): PermissionScope {
  const actions = Object.hasOwn(powers.areaPermissions, area) ? powers.areaPermissions[area] : undefined;
  const saved = actions && Object.hasOwn(actions, action) ? actions[action] : undefined;
  return PERMISSION_SCOPE_KEYS.find((scope) => scope === saved) ?? "none";
}

/** Un elenco; le voci con una virgola dentro (o un'altra virgola nel nome) si separano con il punto e virgola. */
export function joinList(items: readonly string[]): string {
  return items.join(items.some((item) => item.includes(",")) ? "; " : ", ");
}

/** Un'etichetta dentro una frase: «Chiamare…» diventa «chiamare…». */
export const lowerFirst = (text: string) => text.charAt(0).toLowerCase() + text.slice(1);

const isSettings = (key: FeatureKey) => key.startsWith("settings.");
const SETTINGS_COUNT = FEATURE_KEYS_CATALOG.filter(isSettings).length;

type Parts = { areas: string[]; can: string | null; settings: string | null };

/** Le frasi di un ruolo, divise per genere: le aree, poi «Può» e le Impostazioni (assenti se non ce n'è). */
function describeParts(t: Translate, role: Powers): Parts {
  const areas = PERMISSION_AREA_KEYS.map((area) => {
    const areaName = t(AREA_LABEL[area]);
    const scopes = areaActions(area).map((action) => [action, scopeOf(role, area, action)] as const);
    if (scopes.every(([, scope]) => scope === "none")) return t("roles.words.noAccess", { area: areaName });
    const actions = scopes.map(([action, scope]) => {
      const clause = CLAUSE[action][scope] ?? CLAUSE[action].none;
      return clause ? t(clause) : "";
    });
    return t("roles.words.area", { area: areaName, actions: actions.join(", ") });
  });

  const has = new Set(role.features);
  const held = FEATURE_GROUPS.flatMap((group) => group.keys).filter((key) => has.has(key));
  const special = held.filter((key) => !isSettings(key));
  const settings = held.filter(isSettings);
  const can = special.length > 0 ? t("roles.words.can", { list: joinList(special.map((key) => lowerFirst(t(FEATURE_LABEL[key])))) }) : null;
  let sections: string | null = null;
  if (settings.length > 0) {
    const list = settings.length === SETTINGS_COUNT ? t("roles.words.allSettings") : joinList(settings.map((key) => t(FEATURE_LABEL[key])));
    sections = t("roles.words.settings", { list });
  }
  return { areas, can, settings: sections };
}

const sentencesOf = (parts: Parts): string[] => [...parts.areas, ...(parts.can ? [parts.can] : []), ...(parts.settings ? [parts.settings] : [])];

/**
 * Le frasi di un ruolo, per esempio «Lead: vede tutti, crea, modifica solo i suoi, non elimina.» e «Può:
 * chiamare e usare la postazione, mandare WhatsApp.». Le aree per prime, nell'ordine del catalogo; le funzioni
 * nell'ordine dei gruppi dei mock-up; le sezioni delle Impostazioni in un elenco a parte («tutte» se ci sono
 * tutte). Niente frase «Può» senza funzioni, né elenco delle Impostazioni senza sezioni.
 */
export function describeRole(t: Translate, role: Powers): string[] {
  return sentencesOf(describeParts(t, role));
}

/**
 * Per ogni ruolo la frase che lo distingue dagli altri, per la colonna «Cosa fa»: quella che dicono meno ruoli,
 * a parità la prima (le aree vengono prima delle funzioni). Con i ruoli di partenza la frase sui Lead è uguale
 * per Admin, Super user e User, e dirla in tutte le righe non direbbe niente. Se ogni frase è di tutti i ruoli
 * non c'è niente che distingua: vale «Può: …», che è ciò che il ruolo permette in più.
 */
export function summarizeRoles(t: Translate, roles: readonly (Powers & { id: string })[]): Map<string, string> {
  const described = roles.map((role) => ({ id: role.id, parts: describeParts(t, role) }));
  const said = new Map<string, number>();
  for (const { parts } of described) for (const sentence of new Set(sentencesOf(parts))) said.set(sentence, (said.get(sentence) ?? 0) + 1);
  const summaries = new Map<string, string>();
  for (const { id, parts } of described) {
    const sentences = sentencesOf(parts);
    const fewest = Math.min(...sentences.map((sentence) => said.get(sentence) ?? 0));
    const chosen = fewest === roles.length && parts.can ? parts.can : sentences.find((sentence) => said.get(sentence) === fewest);
    if (chosen) summaries.set(id, chosen);
  }
  return summaries;
}
